import type { PageHelp } from "../../help/types";

export const profileHelp: PageHelp = {
  title: "My profile",
  intro:
    "Your personal details, the consents you have given, your documents and the family members (dependants) you can enrol in benefits. A new user starts here: until you accept the data-processing consent and save, the other pages and the other tabs of this page stay locked.",
  sections: [
    "Getting around",
    "Profile picture",
    "Personal details",
    "First sign-in",
    "Dependants",
    "Dependant details",
    "Documents",
    "Consents",
  ],
  contexts: {
    "avatar-dialog":
      "Open the Personal details tab and click the round upload button on your picture.",
    "dependent-form":
      'Open the Dependants tab, then click "Add dependant" or the pencil next to a dependant.',
    "dependent-edit": "Open the Dependants tab and click the pencil next to a dependant.",
    "document-chosen":
      'Open the Documents tab and choose a file with "Upload" — the save button appears next to it.',
    "first-sign-in": "Shown only until you have accepted the consent for the first time.",
  },
  tips: [
    "Fields marked with an asterisk are required. The form checks them when you press Save, not while you type, so a missing field is only reported after you save.",
    "Some details may already have been filled in for you by your employer. Check them and correct anything that is wrong before you save.",
  ],
  steps: [
    {
      selector: '[data-help="profile-tabs"]',
      title: "Switch between the parts of your profile",
      body: "Personal details, Consents, Documents and Dependants each have their own tab. The tabs are disabled until you have accepted the consent on the Personal details tab and saved it.",
      section: "Getting around",
    },
    {
      selector: '[data-help="profile-avatar-button"]',
      title: "Change your profile picture",
      body: "Opens a window where you pick one of the ready-made avatars or upload a picture of your own. The picture is shown in the navigation and on this page. Disabled until you have accepted the consent.",
      section: "Profile picture",
    },
    {
      selector: '[data-help="profile-avatar-presets"]',
      title: "Pick a ready-made avatar",
      body: 'Click an avatar to select it, then press "Confirm" to save it as your picture. A tick marks the avatar you are using now.',
      section: "Profile picture",
      context: "avatar-dialog",
    },
    {
      selector: '[data-help="profile-avatar-upload"]',
      title: "Upload your own picture",
      body: "Choose an image file from your device. It is saved as your picture straight away — there is no separate confirm step for an uploaded file.",
      section: "Profile picture",
      context: "avatar-dialog",
    },
    {
      selector: '[data-help="profile-avatar-confirm"]',
      title: "Save the selected avatar",
      body: "Saves the avatar you clicked and closes the window. The button stays disabled until you have selected an avatar.",
      section: "Profile picture",
      context: "avatar-dialog",
    },
    {
      selector: '[data-help="profile-name"]',
      title: "Enter your name",
      body: "First name and last name are required; the middle name is optional. Write them as they appear in your official documents.",
      section: "Personal details",
    },
    {
      selector: '[data-help="profile-birth"]',
      title: "Enter your date and place of birth",
      body: "The date of birth is required and you must be at least 16 years old. Place of birth, gender and personal identification number are optional.",
      section: "Personal details",
    },
    {
      selector: '[data-help="profile-email"]',
      title: "Keep your e-mail address current",
      body: "Required, and it must be a valid e-mail address. This is your contact address; it can differ from the account you sign in with.",
      section: "Personal details",
    },
    {
      selector: '[data-help="profile-phone"]',
      title: "Add your phone numbers",
      body: "The mobile phone number is required; a second phone number is optional. Use the number on which you can be reached about your benefits.",
      section: "Personal details",
    },
    {
      selector: '[data-help="profile-address"]',
      title: "Enter your address",
      body: "Street, house number, building/floor/door and city. These fields are optional.",
      section: "Personal details",
    },
    {
      selector: '[data-help="profile-citizenship"]',
      title: "Choose your citizenship and postcode",
      body: "Citizenship is a searchable list — start typing a country name. The postcode belongs to the address above.",
      section: "Personal details",
    },
    {
      selector: '[data-help="profile-employment"]',
      title: "Check your employment details",
      body: "Workplace, organisational unit and your academic titles before and after the name. All of them are optional.",
      section: "Personal details",
    },
    {
      selector: '[data-help="profile-main-consent"]',
      title: "Accept the data-processing consent",
      body: "Tick the box to agree to the processing of your personal data; the link opens the full consent text. You cannot save your profile without it, and the other pages stay locked until it is saved.",
      section: "First sign-in",
      context: "first-sign-in",
      optional: true,
    },
    {
      selector: '[data-help="profile-save"]',
      title: "Save your personal details",
      body: "Checks the required fields and saves your profile. On your first save it also records your consent, after which Benefits, Employer and Dashboard open and your benefit packages are loaded.",
      section: "Personal details",
    },
    {
      selector: '[data-help="profile-add-dependent"]',
      title: "Add a dependant",
      body: "Opens an empty form for a family member — for example a spouse or a child — whom you can later enrol in benefits that allow dependants. The dependant is saved only when you press Save in the form.",
      section: "Dependants",
    },
    {
      selector: '[data-help="profile-dependent-edit"]',
      title: "Open a dependant's details",
      body: "The pencil next to a dependant opens their details for editing, together with the option to change their picture or delete them.",
      section: "Dependants",
      optional: true,
    },
    {
      selector: '[data-help="profile-dependent-photo"]',
      title: "Change a dependant's picture",
      body: "Choose an image file from your device. It is saved as the dependant's picture straight away.",
      section: "Dependants",
      context: "dependent-edit",
    },
    {
      selector: '[data-help="profile-dependent-type"]',
      title: "Choose how the dependant is related to you",
      body: "Required. Pick the relationship from the list, for example spouse or child.",
      section: "Dependant details",
      context: "dependent-form",
    },
    {
      selector: '[data-help="profile-dependent-name"]',
      title: "Enter the dependant's name",
      body: "First name and last name are required; the middle name is optional. Use the name as it appears in the dependant's official documents.",
      section: "Dependant details",
      context: "dependent-form",
    },
    {
      selector: '[data-help="profile-dependent-birth"]',
      title: "Enter the dependant's date of birth",
      body: "The date of birth is required and cannot be in the future. Place of birth, gender and personal identification number are optional.",
      section: "Dependant details",
      context: "dependent-form",
    },
    {
      selector: '[data-help="profile-dependent-email"]',
      title: "Add the dependant's e-mail address",
      body: "Optional for a dependant. If you enter one, it must be a valid e-mail address, otherwise saving fails.",
      section: "Dependant details",
      context: "dependent-form",
    },
    {
      selector: '[data-help="profile-dependent-address"]',
      title: "Enter the dependant's address",
      body: "Optional. Fill it in when the dependant does not live at your address, so that insurers can reach them.",
      section: "Dependant details",
      context: "dependent-form",
    },
    {
      selector: '[data-help="profile-dependent-save"]',
      title: "Save the dependant",
      body: "Checks the required fields and saves the dependant. The button stays disabled until you have changed something in the form.",
      section: "Dependant details",
      context: "dependent-form",
    },
    {
      selector: '[data-help="profile-dependent-cancel"]',
      title: "Leave the form without saving",
      body: "Closes the form and returns to the list of dependants. Anything you typed since the last save is discarded.",
      section: "Dependant details",
      context: "dependent-form",
    },
    {
      selector: '[data-help="profile-dependent-delete"]',
      title: "Delete a dependant",
      body: "Removes the dependant from your profile after you confirm. Only shown for a dependant that has already been saved.",
      section: "Dependant details",
      context: "dependent-edit",
    },
    {
      selector: '[data-help="profile-document-upload"]',
      title: "Upload a document",
      body: 'Click "Upload" and choose a file from your device, for example a certificate your employer asked for. The file is not stored until you press the save button that appears next to it.',
      section: "Documents",
    },
    {
      selector: '[data-help="profile-document-save"]',
      title: "Store the chosen file",
      body: "Uploads the chosen file to your documents. It then appears in the list below with today's date.",
      section: "Documents",
      context: "document-chosen",
    },
    {
      selector: '[data-help="profile-document-open"]',
      title: "Open a document",
      body: "Opens the document in a new browser tab, where you can read or download it.",
      section: "Documents",
      optional: true,
    },
    {
      selector: '[data-help="profile-document-details"]',
      title: "Show a document's summary",
      body: "Expands the row to show the document's summary and abstract, when they have been filled in. Click again to collapse it.",
      section: "Documents",
      optional: true,
    },
    {
      selector: '[data-help="profile-document-delete"]',
      title: "Delete a document",
      body: "Removes the document after you confirm. A deleted document cannot be restored — upload it again if you need it back.",
      section: "Documents",
      optional: true,
    },
    {
      selector: '[data-help="profile-consent-document"]',
      title: "Read a consent you have given",
      body: "Each row is one consent: the privacy notice and sensitive-data consent from your profile, and the salary-deduction and insurer consents you signed when enrolling in a package. Click the name to open the document; the date shows when you gave it.",
      section: "Consents",
      optional: true,
    },
    {
      selector: '[data-help="profile-revoke-consents"]',
      title: "Revoke your consents",
      body: "After you confirm, every consent you have given is withdrawn and you are signed out. The next time you sign in you have to accept the consent on this page again before the other pages open.",
      section: "Consents",
      optional: true,
    },
  ],
};
